import { useEffect, useRef, useState, type JSX } from 'react';

/**
 * ExecutionPanel — one compact, fixed-height panel for everything the agent is
 * DOING, sitting above the answer it will produce.
 *
 * It replaces a growing list. The trail used to render one row per tool step, so
 * a ten-step build pushed the answer down ten times while the user was reading —
 * the single worst source of layout jumping in a long turn, and the reason
 * execution felt tangled up with content. Here the panel's collapsed height is
 * constant: the CURRENT activity is swapped in place and a counter carries the
 * sense of progress. The full trail is still one click away for anyone who wants
 * to audit what ran.
 *
 * Everything shown is real: the labels are the agent's own tool steps, the
 * counter is a true done/total, and the elapsed clock counts actual seconds. No
 * synthetic percentages, and nothing is invented for models that report nothing.
 */

export interface ExecutionStep {
  id: string;
  label: string;
  done: boolean;
}

interface ExecutionPanelProps {
  /** The agent's real tool steps, in order. */
  steps: ExecutionStep[];
  /** Current activity verb from the stream, used between tool calls. */
  progress?: string | undefined;
  /** True for a deliverable build — adds the determinate bar. */
  isBuild?: boolean | undefined;
}

/** Live seconds since the panel mounted. The agent spends long stretches between
 *  tools; a moving clock is the honest signal that the turn is alive. */
function useElapsed(): number {
  const [sec, setSec] = useState(0);
  const start = useRef(Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setSec(Math.floor((Date.now() - start.current) / 1000)), 1000);
    return () => window.clearInterval(id);
  }, []);
  return sec;
}

export function ExecutionPanel({ steps, progress, isBuild }: ExecutionPanelProps): JSX.Element | null {
  const [open, setOpen] = useState(false);
  const elapsed = useElapsed();

  if (steps.length === 0 && !progress) return null;

  const total = steps.length;
  const done = steps.filter((s) => s.done).length;
  // The live label is the running step; between tools nothing is running, so fall
  // back to the stream's current verb rather than freezing on the last finished
  // step (which read as "still writing" when it was done).
  const running = steps.find((s) => !s.done);
  const label = running?.label ?? progress ?? (total > 0 ? 'Finishing up' : 'Working');
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  return (
    <div className={`exec${open ? ' is-open' : ''}`} aria-live="polite">
      <button
        type="button"
        className="exec__head"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        // Collapsed, this is a status line, not a control worth announcing as one.
        aria-label={total > 0 ? `${label}, step ${done} of ${total}. Show details` : `${label}. Show details`}
      >
        <span className="exec__pulse" aria-hidden="true" />
        <span className="exec__label">{label}</span>
        {total > 0 ? <span className="exec__count">{done}/{total}</span> : null}
        <span className="exec__time">{elapsed}s</span>
        {total > 0 ? (
          <svg className="exec__chev" width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="m6 9 6 6 6-6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ) : null}
      </button>

      {/* Determinate bar for a build — a real done/total ratio, not a fake timer. */}
      {isBuild && total > 0 ? (
        <div className="exec__track" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
          <div className="exec__fill" style={{ width: `${pct}%` }} />
        </div>
      ) : null}

      {open && total > 0 ? (
        <ol className="exec__steps">
          {steps.map((s) => (
            <li key={s.id} className={`exec__step${s.done ? ' is-done' : ' is-active'}`}>
              <span className="exec__dot" aria-hidden="true">
                {s.done ? (
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
                    <path d="M20 6 9 17l-5-5" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ) : null}
              </span>
              <span className="exec__step-label">{s.label}</span>
            </li>
          ))}
        </ol>
      ) : null}
    </div>
  );
}
